import { logger, sanitizeError } from '@heidi/runtime/logger';
import { useAtomValue, useSetAtom } from 'jotai';
import { useCallback, useEffect, useRef } from 'react';
import type { TrackWorkExperienceFeedbackActioned } from '../../../contracts/work-launch-analytics.types';
import { snoozeWorkExperienceFeedback } from '../api/workExperienceFeedbackApi';
import {
  WORK_TRIGGER_QUERY_COUNT,
  workExperienceFeedbackDismissAtom,
  workExperienceFeedbackEligibleAtom,
  workExperienceFeedbackMarkSubmittedAtom,
  workExperienceFeedbackShowAtom,
  workExperienceFeedbackSnoozeAtom,
  workExperienceFeedbackSnoozeCountAtom,
  workExperienceFeedbackTriggeredThisSessionAtom,
  workExperienceFeedbackVisibleAtom,
} from '../state/workExperienceFeedbackAtoms';

interface UseWorkExperienceFeedbackTriggerOptions {
  queryCount: number;
  isStreaming: boolean;
  /** Bound project id — nudge only shows on an existing Work chat. */
  projectId?: string | null;
  /** The app's `work_experience_feedback_actioned` emit. Pass a stable function. */
  trackWorkExperienceFeedbackActioned: TrackWorkExperienceFeedbackActioned;
  /**
   * The app's verdict on whether this deployment can reach Heidi's product
   * team. When false the nudge never fires.
   */
  isProductFeedbackAvailable: boolean;
}

interface UseWorkExperienceFeedbackTriggerResult {
  shouldShowNudge: boolean;
  showNudge: () => void;
  hideNudge: () => void;
  snooze: () => Promise<void>;
  markSubmitted: () => void;
  snoozeCount: number;
}

/**
 * Trigger logic for the Work experience feedback nudge.
 *
 * Same criteria as Evidence (minus Evidence's dedicated release flag):
 * after 2 completed assistant replies, when not streaming, on a bound
 * project chat, if the user hasn't submitted and hasn't snoozed 3×.
 * Visibility of Work itself is already gated by `useIsProjectsEnabled()`.
 */
export const useWorkExperienceFeedbackTrigger = ({
  queryCount,
  isStreaming,
  projectId,
  trackWorkExperienceFeedbackActioned,
  isProductFeedbackAvailable,
}: UseWorkExperienceFeedbackTriggerOptions): UseWorkExperienceFeedbackTriggerResult => {
  const isVisible = useAtomValue(workExperienceFeedbackVisibleAtom);
  const isEligible = useAtomValue(workExperienceFeedbackEligibleAtom);
  const snoozeCount = useAtomValue(workExperienceFeedbackSnoozeCountAtom);
  const triggeredThisSession = useAtomValue(
    workExperienceFeedbackTriggeredThisSessionAtom
  );

  const showNudgeAction = useSetAtom(workExperienceFeedbackShowAtom);
  const hideNudgeAction = useSetAtom(workExperienceFeedbackDismissAtom);
  const snoozeAction = useSetAtom(workExperienceFeedbackSnoozeAtom);
  const markSubmittedAction = useSetAtom(workExperienceFeedbackMarkSubmittedAtom);

  const hasTrackedView = useRef(false);

  const shouldTrigger =
    isProductFeedbackAvailable &&
    Boolean(projectId) &&
    queryCount >= WORK_TRIGGER_QUERY_COUNT &&
    !isStreaming &&
    isEligible &&
    !triggeredThisSession;

  useEffect(() => {
    if (shouldTrigger && !isVisible && !hasTrackedView.current) {
      showNudgeAction();
      hasTrackedView.current = true;

      trackWorkExperienceFeedbackActioned({
        action: 'viewed',
        query_count: queryCount,
        snooze_count: snoozeCount,
        project_id: projectId ?? undefined,
      });
    }
  }, [
    shouldTrigger,
    isVisible,
    showNudgeAction,
    queryCount,
    snoozeCount,
    projectId,
    trackWorkExperienceFeedbackActioned,
  ]);

  const snooze = useCallback(async () => {
    snoozeAction();

    trackWorkExperienceFeedbackActioned({
      action: 'snoozed',
      snooze_count: snoozeCount + 1,
      query_count: queryCount,
      project_id: projectId ?? undefined,
    });

    try {
      await snoozeWorkExperienceFeedback();
    } catch (error) {
      // A fixed message and the sanitised error: never the error's text.
      logger.error('Failed to sync Work experience feedback snooze to backend:', {
        error: sanitizeError(error),
      });
    }
  }, [
    snoozeAction,
    snoozeCount,
    queryCount,
    projectId,
    trackWorkExperienceFeedbackActioned,
  ]);

  const hideNudge = useCallback(() => {
    hideNudgeAction();

    trackWorkExperienceFeedbackActioned({
      action: 'dismissed',
      query_count: queryCount,
      project_id: projectId ?? undefined,
    });
  }, [hideNudgeAction, queryCount, projectId, trackWorkExperienceFeedbackActioned]);

  return {
    shouldShowNudge: isVisible,
    showNudge: showNudgeAction,
    hideNudge,
    snooze,
    markSubmitted: markSubmittedAction,
    snoozeCount,
  };
};
